import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

export type Lang = "en" | "id";
export const LANGS: readonly Lang[] = ["en", "id"];
const TIME_ZONE = "Asia/Jakarta";

/**
 * A dictionary is a nested object of strings in English, with the Indonesian
 * one typed against it so a missing key is a compile error. `{name}` in a
 * string is replaced by `t("key", { name })`.
 */
type Leaves<T, P extends string = ""> = {
  [K in keyof T & string]: T[K] extends string ? `${P}${K}` : Leaves<T[K], `${P}${K}.`>;
}[keyof T & string];

export type DeepStrings<T> = { [K in keyof T]: T[K] extends string ? string : DeepStrings<T[K]> };

export interface I18n<D> {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: Leaves<D>, vars?: Record<string, string | number>) => string;
  formatDate: (iso: string | Date, opts?: Intl.DateTimeFormatOptions) => string;
  formatTime: (iso: string | Date) => string;
  formatNumber: (n: number) => string;
}

function lookup(dict: unknown, key: string): string | undefined {
  let node: unknown = dict;
  for (const part of key.split(".")) {
    if (node && typeof node === "object" && part in node) node = (node as Record<string, unknown>)[part];
    else return undefined;
  }
  return typeof node === "string" ? node : undefined;
}

function interpolate(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (_, k: string) => (k in vars ? String(vars[k]) : `{${k}}`));
}

const locale = (lang: Lang) => (lang === "id" ? "id-ID" : "en-GB");

/** Times are stored in UTC and always shown in Jakarta (docs/frontend-integration.md 8). */
export function formatInJakarta(lang: Lang, iso: string | Date, opts: Intl.DateTimeFormatOptions): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(locale(lang), { timeZone: TIME_ZONE, ...opts }).format(d);
}

/**
 * Builds a typed provider and hook for one app's dictionaries. The choice
 * persists per device and sets `<html lang>`; `initial` is the ?lang= of a
 * link, then the stored choice, then `fallback` when an app sets one (the
 * event's language), else the browser's language.
 */
export function createI18n<D extends object>(
  dicts: { en: D; id: DeepStrings<D> },
  storageKey: string,
  fallback?: Lang,
) {
  const Ctx = createContext<I18n<D> | null>(null);

  function detect(): Lang {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get("lang")?.toLowerCase();
      if (fromUrl === "en" || fromUrl === "id") return fromUrl;
      const stored = window.localStorage.getItem(storageKey);
      if (stored === "en" || stored === "id") return stored;
    } catch {
      /* storage blocked */
    }
    if (fallback) return fallback;
    return navigator.language?.toLowerCase().startsWith("id") ? "id" : "en";
  }

  function I18nProvider({ children, initial }: { children: ReactNode; initial?: Lang }) {
    const [lang, setLangState] = useState<Lang>(() => initial ?? detect());

    useEffect(() => {
      document.documentElement.lang = lang;
      // A ?lang= link is a choice: keep it for the next page and the next visit.
      try {
        window.localStorage.setItem(storageKey, lang);
      } catch {
        /* storage blocked */
      }
    }, [lang]);

    const setLang = useCallback((next: Lang) => setLangState(next), []);

    const value = useMemo<I18n<D>>(() => {
      const dict = dicts[lang];
      return {
        lang,
        setLang,
        t: (key, vars) => interpolate(lookup(dict, key) ?? lookup(dicts.en, key) ?? key, vars),
        formatDate: (iso, opts) =>
          formatInJakarta(lang, iso, opts ?? { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
        formatTime: (iso) => formatInJakarta(lang, iso, { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }),
        formatNumber: (n) => new Intl.NumberFormat(locale(lang)).format(n),
      };
    }, [lang, setLang]);

    return <Ctx value={value}>{children}</Ctx>;
  }

  function useI18n(): I18n<D> {
    const v = use(Ctx);
    if (!v) throw new Error("useI18n outside its provider");
    return v;
  }

  return { I18nProvider, useI18n };
}
